import { useState } from 'react';
import { MinusIcon, PlayIcon, PlusIcon, SpindleStopIcon, TempIcon } from '@/icons';
import { useFeedSpindle, useSendCommand, useSpindleOn } from '@/store/store';
import styles from '@/components/jog/jog.module.css';

export interface SpindleMeterProps {
  /** Initial RPM shown on the meter. */
  initialRpm?: number;
  /** Minimum settable RPM (stepper/setpoint floor). */
  minRpm?: number;
  /** Full-scale RPM (gauge max + last tick label). */
  maxRpm?: number;
  /** RPM increment per stepper press. */
  step?: number;
  /** Spindle temperature (°C) shown in the header. */
  tempC?: number;
  /** Current spindle load (% of nominal) shown on the load bar. */
  load?: number;
}

// Gauge geometry (SVG user units) ------------------------------------------
const CX = 160;
const CY = 158;
const VIEW_W = 320;
const VIEW_H = 290;
const START = 225; // compass deg (0 = top, clockwise) — bottom-left
const SWEEP = 270; // open ~90° at bottom
const TICK_COUNT = 56;

const R_MINOR_IN = 108;
const R_MINOR_OUT = 120;
const R_MAJOR_IN = 102;
const R_MAJOR_OUT = 124;
const R_LABEL = 140;

const COLOR_FILL = '#9747FF';
const COLOR_EMPTY = 'rgba(255, 255, 255, 0.85)';

// Load bar (segmented VU meter, 0 → MAX_LOAD %) ----------------------------
const LOAD_SEGMENTS = 42;
const MAX_LOAD = 120; // overload zone starts at 100%
// Filled segments fade teal → gray across the active load span.
const LOAD_START_RGB: readonly [number, number, number] = [5, 255, 190]; // teal at 0%
const LOAD_END_RGB: readonly [number, number, number] = [138, 138, 166]; // gray at load head
const COLOR_LOAD_OFF = 'rgba(255, 255, 255, 0.12)';
const COLOR_LOAD_OVER = '#F04248';

function pt(compassDeg: number, radius: number) {
  const rad = (compassDeg * Math.PI) / 180;
  return { x: CX + radius * Math.sin(rad), y: CY - radius * Math.cos(rad) };
}

/** Linear-interpolate the teal→gray fill color at fraction t (0..1). */
function loadFillColor(t: number): string {
  const c = Math.min(Math.max(t, 0), 1);
  const r = Math.round(LOAD_START_RGB[0] + (LOAD_END_RGB[0] - LOAD_START_RGB[0]) * c);
  const g = Math.round(LOAD_START_RGB[1] + (LOAD_END_RGB[1] - LOAD_START_RGB[1]) * c);
  const b = Math.round(LOAD_START_RGB[2] + (LOAD_END_RGB[2] - LOAD_START_RGB[2]) * c);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Spindle RPM meter card: radial tick gauge + glassy stepper + load bar. Self-contained / reusable. */
export function SpindleMeter({
  initialRpm = 1600,
  minRpm = 1000,
  maxRpm = 24000,
  step = 100,
  tempC = 38,
  load = 62,
}: SpindleMeterProps): JSX.Element {
  const sendCommand = useSendCommand();
  const { spindleSpeed } = useFeedSpindle();
  // Running state is the machine's authority (reflects M3/M5 from any source —
  // this button, MDI or a program), not local React state. So the button stays
  // correct across tab/page changes and an M5 from elsewhere flips it to Start.
  const running = useSpindleOn();
  // The shown RPM follows the machine's reported spindleSpeed (clamped to the
  // gauge range), so it survives tab/page changes and reflects the real speed
  // instead of resetting to the local default. Before any speed is commanded
  // (spindleSpeed = 0) it falls back to `initialRpm`. While stopped, +/- dials
  // this value locally without sending — spindleSpeed doesn't change, so the
  // sync below never overwrites the dialed value.
  const [rpm, setRpm] = useState(() =>
    spindleSpeed > 0 ? Math.min(Math.max(Math.round(spindleSpeed), minRpm), maxRpm) : initialRpm,
  );
  // Re-sync when the machine's reported speed changes (React's "adjust state
  // during render" pattern — no effect, so no cascading render).
  const [prevSpeed, setPrevSpeed] = useState(spindleSpeed);
  if (spindleSpeed !== prevSpeed) {
    setPrevSpeed(spindleSpeed);
    if (spindleSpeed > 0) setRpm(Math.min(Math.max(Math.round(spindleSpeed), minRpm), maxRpm));
  }
  const progress = Math.min(Math.max((rpm - minRpm) / (maxRpm - minRpm), 0), 1);

  // play → M3 S<rpm> (spindle CW at set RPM), Stop → M5 (spindle off).
  const handleToggle = () => {
    sendCommand({ type: 'MDI', command: running ? 'M5' : `M3 S${rpm}` });
  };

  // Stepper: clamp to [minRpm, maxRpm]. Running → re-issue M3 S<rpm> to change the
  // speed live. Stopped → only update the local setpoint; nothing is sent to the
  // machine until Start, so dialing the gauge never commands the spindle.
  const changeRpm = (next: number) => {
    const clamped = Math.min(Math.max(next, minRpm), maxRpm);
    setRpm(clamped);
    if (running) sendCommand({ type: 'MDI', command: `M3 S${clamped}` });
  };

  const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => {
    const f = i / TICK_COUNT;
    const angle = START + f * SWEEP;
    const major = i === 0 || i === TICK_COUNT / 2 || i === TICK_COUNT;
    const rIn = major ? R_MAJOR_IN : R_MINOR_IN;
    const rOut = major ? R_MAJOR_OUT : R_MINOR_OUT;
    const a = pt(angle, rIn);
    const b = pt(angle, rOut);
    return { f, a, b, major, filled: f <= progress + 1e-6 };
  });

  const labels = [
    { f: 0, text: String(minRpm) },
    { f: 0.5, text: String(Math.round((minRpm + maxRpm) / 2)) },
    { f: 1, text: String(maxRpm) },
  ].map(({ f, text }) => ({ text, ...pt(START + f * SWEEP, R_LABEL) }));

  return (
    <div className={`${styles.jogCard} ${styles.jogSpindleCard}`}>
      {/* Header: title + temperature */}
      <div className={styles.jogSpindleHeader}>
        <span className={styles.jogCardTitle}>Spindle RPM</span>
        <span className={styles.spindleTemp}>
          <TempIcon width={24} height={24} />
          {tempC}°C
        </span>
      </div>
      <hr className={styles.jogCardDivider} />

      <div className={styles.spindleGauge}>
        <svg className={styles.spindleGaugeSvg} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}>
          {/* Radial ticks */}
          {ticks.map((t, i) => (
            <line
              key={i}
              x1={t.a.x}
              y1={t.a.y}
              x2={t.b.x}
              y2={t.b.y}
              stroke={t.filled ? COLOR_FILL : COLOR_EMPTY}
              strokeWidth={t.major ? 3.5 : 2}
              strokeLinecap="round"
            />
          ))}

          {/* Scale labels */}
          {labels.map((l) => (
            <text
              key={l.text}
              x={l.x}
              y={l.y}
              fill="#ffffff"
              fontSize="14"
              letterSpacing="0.06em"
              textAnchor="middle"
              dominantBaseline="middle"
              fontFamily="PT Mono"
            >
              {l.text}
            </text>
          ))}
        </svg>

        {/* Center overlay: value + label */}
        <div className={styles.spindleGaugeCenter}>
          <span className={styles.spindleValue}>{rpm}</span>
          <span className={styles.spindleSetLabel}>Set RPM</span>
        </div>

        {/* Start / Stop — sits in the gauge opening, centered between the 0 and 3000 labels */}
        <button
          className={`${styles.spindleAction} ${running ? styles.spindleActionStop : styles.spindleActionStart}`}
          type="button"
          onClick={handleToggle}
          aria-label={running ? 'Stop spindle' : 'Start spindle'}
        >
          {running ? (
            <SpindleStopIcon width={14} height={14} />
          ) : (
            <PlayIcon width={14} height={14} />
          )}
          {running ? 'Stop' : 'Start'}
        </button>
      </div>

      {/* Glassy stepper bar */}
      <div className={styles.spindleStepper}>
        <button
          className={styles.spindleStepperBtn}
          type="button"
          onClick={() => changeRpm(rpm - step)}
          aria-label="Decrease RPM"
        >
          <MinusIcon width={24} height={24} />
        </button>
        <span className={styles.spindleStepperValue}>{rpm}</span>
        <button
          className={styles.spindleStepperBtn}
          type="button"
          onClick={() => changeRpm(rpm + step)}
          aria-label="Increase RPM"
        >
          <PlusIcon width={24} height={24} />
        </button>
      </div>

      {/* Spindle load bar */}
      <div className={styles.spindleLoad}>
        <div className={styles.spindleLoadHeader}>
          <span className={styles.spindleLoadLabel}>Spindle Load</span>
          <span className={styles.spindleLoadPct}>{load}%</span>
        </div>
        <div className={styles.spindleLoadBar}>
          {Array.from({ length: LOAD_SEGMENTS }, (_, i) => {
            const percent = ((i + 0.5) / LOAD_SEGMENTS) * MAX_LOAD;
            const color =
              percent > 100
                ? COLOR_LOAD_OVER
                : percent <= load
                  ? loadFillColor(load > 0 ? percent / load : 0)
                  : COLOR_LOAD_OFF;
            return <span key={i} className={styles.spindleLoadSeg} style={{ background: color }} />;
          })}
        </div>
        <div className={styles.spindleLoadScale}>
          <span>0</span>
          <span>100</span>
          <span>{MAX_LOAD}%</span>
        </div>
      </div>
    </div>
  );
}
